/**
 * The agent turns this person has running right now, across every thread and
 * page. A turn runs on the server detached from the browser, so leaving the
 * thread does not end it; this is how the rest of the app knows it is still
 * going (the sidebar indicator) and when it stops.
 *
 * Fed by `useAgentChat`: any thread update it sees with this person's lock is
 * tracked, and one without a lock is untracked. Threads nobody has on screen
 * are polled by `RunningTurnsWatcher` so a turn finishing off-page is noticed
 * too. Outcomes are not raised here: a turn that ends with nobody watching
 * becomes a server-owned signal (`AgentAttentionProvider`). This store only
 * tracks what is running, and feeds the Agents menu's Working section.
 */
import { create } from "zustand";

import type { AgentScope } from "./types";

export type RunningTurn = {
  threadId: string;
  /** The lock's `turn_id`; a new id on the same thread is a new turn. */
  turnId: string;
  scope: Pick<AgentScope, "kind" | "id">;
  title: string | null;
  startedAt: string | null;
  /** The agent's latest progress label (`active_turn.status`). */
  status?: string | null;
  statusAt?: string | null;
};

type State = {
  /** Newest first. */
  turns: RunningTurn[];
  /** Threads currently on screen (ref-counted by mounted chats). */
  viewed: Record<string, number>;
};

type Actions = {
  track: (turn: RunningTurn) => void;
  untrack: (threadId: string) => void;
  view: (threadId: string) => void;
  unview: (threadId: string) => void;
  clear: () => void;
};

export const useRunningTurnsStore = create<State & Actions>()((set) => ({
  turns: [],
  viewed: {},
  track: (turn) =>
    set((cur) => {
      const existing = cur.turns.find((t) => t.threadId === turn.threadId);
      if (existing && existing.turnId === turn.turnId) {
        // Same turn: keep its place, refresh what may have changed.
        if (
          existing.title === turn.title &&
          (existing.status ?? null) === (turn.status ?? null) &&
          (existing.statusAt ?? null) === (turn.statusAt ?? null)
        )
          return cur;
        return {
          turns: cur.turns.map((t) =>
            t.threadId === turn.threadId
              ? {
                  ...t,
                  title: turn.title,
                  status: turn.status ?? null,
                  statusAt: turn.statusAt ?? null
                }
              : t
          )
        };
      }
      return {
        turns: [turn, ...cur.turns.filter((t) => t.threadId !== turn.threadId)]
      };
    }),
  untrack: (threadId) =>
    set((cur) =>
      cur.turns.some((t) => t.threadId === threadId)
        ? { turns: cur.turns.filter((t) => t.threadId !== threadId) }
        : cur
    ),
  view: (threadId) =>
    set((cur) => ({
      viewed: { ...cur.viewed, [threadId]: (cur.viewed[threadId] ?? 0) + 1 }
    })),
  unview: (threadId) =>
    set((cur) => {
      const count = (cur.viewed[threadId] ?? 0) - 1;
      const viewed = { ...cur.viewed };
      if (count > 0) viewed[threadId] = count;
      else delete viewed[threadId];
      return { viewed };
    }),
  clear: () => set({ turns: [], viewed: {} })
}));

/** Imperative access for code outside React (the chat hook's callbacks). */
export const runningTurnsStore = {
  track: (turn: RunningTurn) => useRunningTurnsStore.getState().track(turn),
  untrack: (threadId: string) =>
    useRunningTurnsStore.getState().untrack(threadId),
  view: (threadId: string) => useRunningTurnsStore.getState().view(threadId),
  unview: (threadId: string) =>
    useRunningTurnsStore.getState().unview(threadId),
  isViewed: (threadId: string) =>
    (useRunningTurnsStore.getState().viewed[threadId] ?? 0) > 0,
  turns: () => useRunningTurnsStore.getState().turns,
  clear: () => useRunningTurnsStore.getState().clear()
};

/** The turns nobody has on screen: the watcher polls these. */
export const selectOffscreenTurns = (state: State): RunningTurn[] =>
  state.turns.filter((t) => !(state.viewed[t.threadId] > 0));
